import { Component, Inject, Input, Optional, OnInit } from "@angular/core";
import { BaseFormComponent } from "../../../../shared/components/base/base-form.component";
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from "@angular/forms";
import { MasterModel } from "../../../../master/models/master.model";
import { PreinvoiceService } from "../../../../../modules/invoice/preinvoice/preinvoice.service";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActualSelected, Utils } from "../../../../../shared/utils";
import { ContactService } from "../../../../../modules/contact/contact/contact.service";
import { ContactFormV2Component } from "../../../../contacts/components/contact-form-v2/contact-form-v2.component";
import { consts } from "../../../../../consts";
import { debounceTime, takeUntil } from "rxjs/operators";
import { firstValueFrom, Subject } from "rxjs";
import { PreinvoiceLookupsFacade } from "../../../preinvoice/models/preinvoice-lookups.facade";
import { PreinvoiceFormFactory } from "../../../preinvoice/models/preinvoice-form.factory";
import { PreinvoiceFormUtils } from "../../../preinvoice/models/preinvoice-form.utils";
import { PreinvoicePaymentValidators } from "../../../preinvoice/models/preinvoice-payment.validators";
import { PreinvoiceSaveMapper } from "../../../preinvoice/models/preinvoice-save.mapper";
import moment from "moment";
import { ActivityService } from "../../../../../modules/user/activity/activity.service";
import { MasterConceptModel } from "../../../../master/models/master-concept.model";
import { ConceptSerialNumberComponent } from "../../../../../modules/concept/concept/serialNumber/concept-serialNumber.component";
import { ConceptService } from "../../../../../modules/concept/concept/concept.service";
import { UserContactService } from "../../../../../modules/user/usercontact/usercontact.service";
import { ActivatedRoute, Router } from "@angular/router";
import { PreinvoiceFormAdapter } from "../../../preinvoice/models/preinvoice-form.adapter";
import { delay } from 'rxjs/operators';
import { LoadingDialogService } from "../../../../../shared/ui/loading/loading-dialog.service";

@Component({
    selector: 'app-recurrent-form',
    templateUrl: './recurrent-form.components.html',
    styleUrls: ['./recurrent-form.components.css'],
    standalone: false
})

export class RecurrentFormV2Component extends BaseFormComponent implements OnInit {
  @Input() mode: 'page' | 'modal' | 'panel' = 'page';

  form: UntypedFormGroup;

  isSaving = false;
  isDeleting = false;
  isLoading = true;
  submitted = false;
  isEditRoute = false;

  readonly frequencyOptions = [
    { label: 'Cada X días', value: 'dias' },
    { label: 'Semanal', value: 'semana' },
    { label: 'Mensual', value: 'mes' },
    { label: 'Trimestral', value: 'trimestre' },
    { label: 'Semestral', value: 'semestre' },
    { label: 'Anual', value: 'año' },
  ];

  contacts: MasterModel[] = [];
  payMethods: MasterModel[] = [];
  bankAccounts: MasterModel[] = [];
  cashRegisters: MasterModel[] = [];
  partners: MasterModel[] = [];
  taxes: MasterModel[] = [];
  concepts: MasterModel[] = [];
  projects: MasterModel[] = [];
  isProjectEnable = false;
  unitMeasurable: MasterModel[] = [];
  accountingModels: MasterModel[] = [];
  centers: MasterModel[] = [];
  userCompany: any;

  private contactTaxClientUid: string | null = null;
  private contactTaxClientSecondUid: string | null = null;
  private contactExpirationDays: number | null = null;
  private readonly lazyLookups = new Map<string, Promise<void>>();

  private destroy$ = new Subject<void>();

  subtotal = 0;
  taxesTotal = 0;
  retentionsTotal = 0;
  total = 0;
  taxesLabel = 'Impuestos';
  lineTotals: number[] = [];
  private taxById = new Map<any, any>();
  private readonly CHUNK = 30;
  visibleCount = this.CHUNK;

  constructor(
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private preinvoiceService: PreinvoiceService,
    protected snackBar: MatSnackBar,
    protected dialog: MatDialog,
    private lookupsFacade: PreinvoiceLookupsFacade,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    @Optional() public dialogRef: MatDialogRef<RecurrentFormV2Component>,

    private contactService: ContactService,
    private activityService: ActivityService,
    private conceptService: ConceptService,
    private userContactService: UserContactService,
    private loading: LoadingDialogService,
  ) {
    super(dialog, snackBar);

    if (data && data.mode) {
      this.mode = data.mode;
    }

    this.form = PreinvoiceFormFactory.buildForm(this.fb);
    this.addRecurrentControls();
    this.setIssuer();
  }

  async ngOnInit() {
    this.loadLookups();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getData() {
    let self = this;
    this.route.params.subscribe(params => {
      const uid = params['uid'];
      self.isEditRoute = uid != null;
      if (uid != null) {
        self.preinvoiceService.loadData(uid)
          .then(data => self.loadDataAndApplyToForm(data))
          .catch(() => {
            self.isLoading = false;
            self.notification.errorBottom('No se pudo cargar la factura recurrente.');
            self.router.navigate(['invoice/recurrents']);
          });
      } else {
        self.loadActivity();
      }
    });
  }

  private async loadDataAndApplyToForm(api: any): Promise<void> {
    await Promise.all([
      api && api.receiverUid ? this.loadCentersForContactAsync(api.receiverUid) : Promise.resolve(),
      this.ensurePaymentLookups(api ? api.typePay : ''),
      api && api.projectUid ? this.ensureProjects() : Promise.resolve(),
    ]);

    this.applyApiToForm(api);
    this.isLoading = false;
  }

  get items(): UntypedFormArray {
    return PreinvoiceFormFactory.items(this.form);
  }

  get visibleItems(): any[] {
    return this.items.controls.slice(0, this.visibleCount);
  }

  onLinesScroll(ev: any): void {
    const el = ev && ev.target;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200 && this.visibleCount < this.items.length) {
      this.visibleCount = Math.min(this.visibleCount + this.CHUNK, this.items.length);
    }
  }

  private loadLookups(): void {
    let self = this;
    this.isLoading = true;

    this.lookupsFacade.loadAll$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        function (l: any) {
          self.contacts = (l && l.contacts) ? l.contacts : [];
          self.payMethods = (l && l.payMethods) ? l.payMethods : [];
          self.bankAccounts = (l && l.bankAccounts) ? l.bankAccounts : [];
          self.cashRegisters = (l && l.cashRegisters) ? l.cashRegisters : [];
          self.partners = (l && l.partners) ? l.partners : [];
          self.taxes = (l && l.taxes) ? l.taxes : [];
          self.buildTaxIndex();
          self.concepts = (l && l.concepts) ? l.concepts : [];
          self.projects = (l && l.projects) ? l.projects : [];
          self.unitMeasurable = (l && l.unitMeasurable) ? l.unitMeasurable : [];
          self.accountingModels = (l && l.accountingModels) ? l.accountingModels : [];
          self.centers = (l && l.centers) ? l.centers : [];
          self.userCompany = (l && l.userCompany) ? l.userCompany : null;

          self.initFromData();

          self.form.get('issuerName').setValue(self.userCompany.companyName);
          self.form.get('issuerVatNumber').setValue(self.userCompany.vatNumber);
          self.applyDefaultEntranceAccount();

          self.wirePaymentValidators();
          self.wireExpiration();
          self.wireFrequency();
          self.ensureOneLine();
          self.wireTotals();
          self.recomputeTotals();

          self.getData();

        },
        function () {
          self.isLoading = false;
          self.notification.errorBottom('Error cargando datos del formulario.');
        }
      );
  }

  private initFromData(): void {
    if (!this.data) {
      return;
    }
    if (this.data.uid) {
      return;
    }

    if (this.data.form) {
      this.form = this.data.form;
      return;
    }
  }

  applyDefaultEntranceAccount() {
    const self = this;

    if (Utils.IsNullOrEmpty(self.form.get('entranceAccount').value)) {
      let account = null;
      if (this.userCompany.isSalesInvoice) {
        account = self.accountingModels.filter(c => c.metadata != null && c.metadata.number == '7000000001')[0];
      }

      if (this.userCompany.isServiceInvoice) {
        account = self.accountingModels.filter(c => c.metadata != null && c.metadata.number == '7050000001')[0];
      }

      if (this.userCompany.isSalesInvoice && this.userCompany.isServiceInvoice) {
        account = self.accountingModels.filter(c => c.metadata != null && c.metadata.number == '7000000001')[0];
      }

      if (account) {
        self.form.patchValue({ entranceAccount: { id: account.id, display: account.display } });
      }

    }
  }

  private applyApiToForm(api: any): void {
    let mapped = PreinvoiceFormAdapter.toFormPatch({
      api: api,
      payMethods: this.payMethods,
      taxes: this.taxes,
      concepts: this.concepts,
      contacts: this.contacts,
      cashRegisters: this.cashRegisters,
      bankAccounts: this.bankAccounts,
      partners: this.partners,
      projects: this.projects,
      accountingModels: this.accountingModels,
      masterUnitsMeasurables: this.unitMeasurable,
      centers: this.centers
    });

    this.form.patchValue(mapped.patch, { emitEvent: false });
    this.form.patchValue({
      recurrentNumber: api.recurrentNumber || '',
      recurrentStart: api.recurrentStart ? new Date(api.recurrentStart) : null,
      recurrentFrequency: api.recurrentFrequency || null,
      recurrentFrequencyDays: api.recurrentFrequencyDays || null,
    }, { emitEvent: false });
    this.applyFrequencyValidators();
    this.visibleCount = this.CHUNK;

    let arr = this.items;
    arr.clear({ emitEvent: false });

    for (let i = 0; i < mapped.items.length; i++) {
      let it = mapped.items[i];

      if (it.type === 'title') {
        let fgTitle = PreinvoiceFormFactory.createTitle(this.fb);
        fgTitle.patchValue({ title: it.title || '' }, { emitEvent: false });
        arr.push(fgTitle, { emitEvent: false });
      } else {
        let fgLine = PreinvoiceFormFactory.createLine(this.fb);
        fgLine.patchValue(it, { emitEvent: false });
        arr.push(fgLine, { emitEvent: false });
      }
    }
    this.recomputeTotals();
  }

  private setIssuer() {
    let self = this;

    this.userContactService.loadData().then(userContact => {
      if (Utils.IsNullOrEmpty(self.form.get('issuerAddress').value)) self.form.get('issuerAddress').setValue(userContact.address);
      if (Utils.IsNullOrEmpty(self.form.get('issuerPostalCode').value)) self.form.get('issuerPostalCode').setValue(userContact.postalCode);
      if (Utils.IsNullOrEmpty(self.form.get('issuerLocation').value)) self.form.get('issuerLocation').setValue(userContact.location);
      if (Utils.IsNullOrEmpty(self.form.get('issuerCountry').value)) self.form.get('issuerCountry').setValue(userContact.country);
      if (Utils.IsNullOrEmpty(self.form.get('issuerState').value)) self.form.get('issuerState').setValue(userContact.state);
    });
  }

  private ensureOneLine(): void {
    if (this.items.length === 0) this.addLine();
  }

  addLine(): void {
    this.items.push(PreinvoiceFormFactory.createLine(this.fb));
    this.visibleCount = this.items.length;
    this.recomputeTotals();
  }

  removeLine(i: number): void {
    this.items.removeAt(i);
    this.recomputeTotals();
  }

  dropLine(event: any): void {
    let moved = this.items.at(event.previousIndex);
    this.items.removeAt(event.previousIndex);
    this.items.insert(event.currentIndex, moved);
    this.recomputeTotals();
  }
  private wirePaymentValidators(): void {
    let self = this;
    let payCtrl = this.form.get('payMethodUid');
    if (!payCtrl) return;

    payCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(function () {
      self.applyPaymentValidators();
      self.ensurePaymentLookups(self.getPayTypeLabel());
    });

    this.applyPaymentValidators();
  }

  private applyPaymentValidators(): void {
    let payLabel = this.getPayTypeLabel();
    PreinvoicePaymentValidators.apply(this.form, payLabel, consts);
  }

  private addRecurrentControls(): void {
    this.form.addControl('recurrentNumber', this.fb.control({ value: '', disabled: true }));
    this.form.addControl('recurrentStart', this.fb.control(moment().add(1, 'day').startOf('day').toDate(), [Validators.required, RecurrentFormV2Component.notBeforeToday]));
    this.form.addControl('recurrentFrequency', this.fb.control('mes', Validators.required));
    this.form.addControl('recurrentFrequencyDays', this.fb.control(null));
  }

  private static notBeforeToday(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    const value = moment(control.value);
    return value.isValid() && value.isBefore(moment(), 'day') ? { beforeToday: true } : null;
  }

  private wireFrequency(): void {
    this.form.get('recurrentFrequency').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyFrequencyValidators());
    this.applyFrequencyValidators();
  }

  private applyFrequencyValidators(): void {
    const days = this.form.get('recurrentFrequencyDays');
    if (this.form.get('recurrentFrequency').value === 'dias') {
      days.setValidators([Validators.required, Validators.min(1)]);
    } else {
      days.clearValidators();
      days.setValue(null, { emitEvent: false });
    }
    days.updateValueAndValidity({ emitEvent: false });
  }

  get startBeforeToday(): boolean {
    const ctrl = this.form.get('recurrentStart');
    return !!(ctrl && ctrl.hasError('beforeToday'));
  }

  get scheduleSummary(): string {
    const start = this.form.get('recurrentStart').value;
    const frequency = this.form.get('recurrentFrequency').value;
    if (!start || !frequency || !moment(start).isValid()) return '';

    const days = Number(this.form.get('recurrentFrequencyDays').value);
    const texts: any = {
      dias: days > 0 ? `cada ${days} ${days === 1 ? 'día' : 'días'}` : '',
      semana: 'cada semana',
      mes: 'cada mes',
      trimestre: 'cada trimestre',
      semestre: 'cada semestre',
      'año': 'cada año',
    };
    const every = texts[frequency];
    if (!every) return '';
    return `La primera factura se generará el ${moment(start).format('DD/MM/YYYY')} y después ${every}.`;
  }

  private linesWithoutIndirectTax(): number[] {
    const result: number[] = [];
    const controls = this.items.controls || [];
    for (let i = 0; i < controls.length; i++) {
      const value = controls[i].value;
      if (!value || value.type !== 'line') continue;
      const hasIndirect = (value.tax || []).some((tax: any) => {
        const full = this.findTaxFull(tax);
        const type = String((full && full.metadata && full.metadata.type) || (tax && tax.type) || '').toUpperCase();
        return type.indexOf('IVA') !== -1 || type.indexOf('IGIC') !== -1;
      });
      if (!hasIndirect) result.push(i + 1);
    }
    return result;
  }

  private wireExpiration(): void {
    const releaseCtrl = this.form.get('recurrentStart');
    const expirationCtrl = this.form.get('invoiceExpiration');
    if (!releaseCtrl || !expirationCtrl) return;

    releaseCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((release: any) => {
      if (!release) return;
      const releaseDate = moment(release);
      if (!releaseDate.isValid()) return;

      if (this.contactExpirationDays != null) {
        expirationCtrl.setValue(releaseDate.clone().add(this.contactExpirationDays, 'days').toDate());
        return;
      }

      const expiration = expirationCtrl.value ? moment(expirationCtrl.value) : null;
      if (!expiration || !expiration.isValid() || expiration.isBefore(releaseDate, 'day')) {
        expirationCtrl.setValue(releaseDate.toDate());
      }
    });
  }

  get expirationBeforeStart(): boolean {
    const release = this.form.get('recurrentStart').value;
    const expiration = this.form.get('invoiceExpiration').value;
    if (!release || !expiration) return false;
    return moment(expiration).isBefore(moment(release), 'day');
  }

  private hasConceptLines(): boolean {
    const items = this.items.controls || [];
    return items.some(item => item.get('type') && item.get('type').value === 'line');
  }

  private showValidationErrors(): void {
    const messages = PreinvoiceFormFactory.getValidationMessages(this.form)
      .filter(message => message !== 'Indica la fecha de creación.')
      .map(message => message === 'Indica la fecha de expiración.' ? 'Indica la fecha de vencimiento.' : message);
    if (!this.hasConceptLines()) messages.unshift('Añade al menos una línea de concepto.');
    if (this.form.get('recurrentStart').hasError('required')) messages.push('Indica el inicio de facturación.');
    if (this.startBeforeToday) messages.push('El inicio de facturación no puede ser anterior a hoy.');
    if (this.form.get('recurrentFrequency').invalid) messages.push('Selecciona la frecuencia.');
    if (this.form.get('recurrentFrequencyDays').invalid) messages.push('Indica cada cuántos días se factura.');
    if (this.expirationBeforeStart) messages.push('La fecha de vencimiento no puede ser anterior al inicio de facturación.');
    this.linesWithoutIndirectTax().forEach(line => messages.push(`Línea ${line}: incluye IVA o IGIC.`));

    const shown = messages.slice(0, 6).map(message => '• ' + message);
    const rest = messages.length - shown.length;
    if (rest > 0) shown.push(`• y ${rest} más`);
    this.notification.errorBottom(shown.length ? 'Revisa los campos marcados en rojo:\n' + shown.join('\n') : 'Revisa los campos marcados en rojo.');

    this.visibleCount = this.items.length;
    setTimeout(() => {
      const firstInvalid = document.querySelector('app-recurrent-form .mat-form-field-invalid, app-recurrent-form .mat-mdc-form-field-invalid, app-recurrent-form .pf-empty-lines--invalid');
      if (firstInvalid) firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  private ensureLookup(key: string, load: () => Promise<void>): Promise<void> {
    let entry = this.lazyLookups.get(key);
    if (!entry) {
      entry = load().catch(() => {
        this.lazyLookups.delete(key);
      });
      this.lazyLookups.set(key, entry);
    }
    return entry;
  }

  private ensurePaymentLookups(payLabel: string): Promise<void> {
    const tasks: Promise<void>[] = [];
    if (PreinvoicePaymentValidators.isBankAccountPayment(payLabel, consts)) {
      tasks.push(this.ensureLookup('bankAccounts', () => firstValueFrom(this.lookupsFacade.loadBankAccounts$()).then(list => { this.bankAccounts = list || []; })));
    }
    if (PreinvoicePaymentValidators.isCashRegisterPayment(payLabel)) {
      tasks.push(this.ensureLookup('cashRegisters', () => firstValueFrom(this.lookupsFacade.loadCashRegisters$()).then(list => { this.cashRegisters = list || []; })));
    }
    if (PreinvoicePaymentValidators.isPartnerPayment(payLabel)) {
      tasks.push(this.ensureLookup('partners', () => firstValueFrom(this.lookupsFacade.loadPartners$()).then(list => { this.partners = list || []; })));
    }
    return Promise.all(tasks).then(() => undefined);
  }

  private ensureProjects(): Promise<void> {
    return this.ensureLookup('projects', () => firstValueFrom(this.lookupsFacade.loadProjects$()).then(list => { this.projects = list || []; }));
  }

  private getPayTypeLabel(): string {
    let pm = PreinvoiceFormUtils.resolveSelected(this.payMethods, this.form.value.payMethodUid);
    if (!pm) return '';
    return pm.display || pm.name || '';
  }

  isBankAccountPayment(): boolean {
    if (!this.form.value.payMethodUid) return false;
    return PreinvoicePaymentValidators.isBankAccountPayment(this.form.value.payMethodUid.id, consts);
  }

  isCashRegisterPayment(): boolean {
    if (!this.form.value.payMethodUid) return false;
    return PreinvoicePaymentValidators.isCashRegisterPayment(this.form.value.payMethodUid.id);
  }

  isPartnerPayment(): boolean {
    if (!this.form.value.payMethodUid) return false;
    return PreinvoicePaymentValidators.isPartnerPayment(this.form.value.payMethodUid.id);
  }

  onSave(): void {
    let self = this;
    if (this.isSaving) return;

    this.submitted = true;
    this.applyPaymentValidators();
    this.form.markAllAsTouched();

    if (this.form.invalid || this.expirationBeforeStart || !this.hasConceptLines() || this.linesWithoutIndirectTax().length > 0) {
      this.showValidationErrors();
      return;
    }

    this.isSaving = true;

    try {
      let fv = this.form.getRawValue();
      this.recomputeTotals();

      if (Utils.IsNullOrEmpty(fv.entranceAccount)) {
        throw new Error('Debe especificar la cuenta contable de ingreso.');
      }

      let contact = PreinvoiceFormUtils.resolveSelected(this.contacts, fv.contactUid);
      if (!contact || !contact.id) throw new Error('Debe especificar un contacto válido.');

      let payType = self.form.get('payMethodUid').value;
      if (!payType) throw new Error('Debe especificar la forma de cobro.');

      let bank = PreinvoiceFormUtils.resolveSelected(this.bankAccounts, fv.bankAccountUid);
      let cash = fv.cashRegisterUid != null && this.cashRegisters.filter(c => c.id == fv.cashRegisterUid.id)[0] != null ? { id: this.cashRegisters.filter(c => c.id == fv.cashRegisterUid.id)[0].metadata.id } : null;
      let partner = PreinvoiceFormUtils.resolveSelected(this.partners, fv.partnerUid);
      let project = PreinvoiceFormUtils.resolveSelected(this.projects, fv.projectUid);
      let accountingModel = PreinvoiceFormUtils.resolveSelected(this.accountingModels, fv.entranceAccount);
      let center = PreinvoiceFormUtils.resolveSelected(this.centers, fv.centerUid);

      if (this.isBankAccountPayment() && (!bank || !bank.id)) throw new Error('Debe especificar la cuenta bancaria.');
      if (this.isCashRegisterPayment() && (!cash || !cash.id)) throw new Error('Debe especificar la caja/efectivo.');
      if (this.isPartnerPayment() && (!partner || !partner.id)) throw new Error('Debe especificar el socio.');

      let mapped = PreinvoiceSaveMapper.mapItemsToGroupsAndLines({
        items: fv.items,
        concepts: this.concepts,
        taxes: this.taxes
      });

      let invoiceLines = this.buildApiInvoiceLines(mapped.invoiceLines, fv.items, false);

      let dto: any = {
        invoiceLines: invoiceLines,

        invoiceRelease: new Date(),
        invoiceExpiration: fv.invoiceExpiration,

        recurrentNumber: fv.recurrentNumber || 'REC',
        recurrentStart: moment(fv.recurrentStart).format('YYYY-MM-DD'),
        recurrentFrequency: fv.recurrentFrequency,
        recurrentFrequencyDays: fv.recurrentFrequency === 'dias' ? Number(fv.recurrentFrequencyDays) : 0,

        issuerAddress: fv.issuerAddress,
        issuerPostalCode: fv.issuerPostalCode,
        issuerLocation: fv.issuerLocation,
        issuerCountry: fv.issuerCountry,
        issuerState: fv.issuerState,
        issuerName: fv.issuerName,
        issuerVatNumber: fv.issuerVatNumber,
        comments: fv.comments,

        entranceAccount: accountingModel ? accountingModel.display : (fv.entranceAccount && typeof fv.entranceAccount === 'object' ? (fv.entranceAccount.display || fv.entranceAccount.id || null) : fv.entranceAccount),

        receiverName: (contact.display || contact.name || 'Factura simplificada'),
        receiverUid: contact.id,

        centerUid: center ? center.id : '00000000-0000-0000-0000-000000000000',

        expenseAccount: null,

        typePay: payType.id,

        partnersId: partner ? partner.id : 0,
        projectUid: project ? project.id : null,

        useDeliverAddress: false,
        numberKilometers: null,

        fiscalYear: Number(ActualSelected.yearSelected),
        activityUid: ActualSelected.activitySelected,

        total: Number(this.total) || 0
      };

      if (this.isBankAccountPayment() && bank) {
        dto.typePayBankAccount = bank.id;
      }

      if (this.isCashRegisterPayment() && cash) {
        dto.efectivoId = cash.id;
      }

      dto.invoiceLineGroups = mapped.lineGroups;

      let p: Promise<any>;
      if (!fv.uid) p = this.preinvoiceService.addData(dto);
      else p = this.preinvoiceService.editData(fv.uid, dto);

      this.loading.run(fv.uid ? 'Guardando factura recurrente…' : 'Creando factura recurrente…', p).then(function () {
        self.isSaving = false;
        self.notification.okBottom('Factura recurrente guardada correctamente');
        self.router.navigate(['invoice/recurrents']);
      }).catch(function (e: any) {
        self.isSaving = false;
        self.notification.errorBottom(e && e.error && e.error.message ? e.error.message : e && e.message ? e.message : 'Error guardando la factura recurrente.');
      });

    } catch (e) {
      this.isSaving = false;
      this.notification.errorBottom(e && e.message ? e.message : 'Error guardando la factura recurrente.');
    }
  }

  private buildApiInvoiceLines(mappedLines: any[], uiItems: any[], isMeasurableFlag: boolean): any[] {
    let result: any[] = [];

    let uiLines: any[] = [];
    for (let i = 0; i < (uiItems || []).length; i++) {
      if (uiItems[i] && uiItems[i].type === 'line') uiLines.push(uiItems[i]);
    }

    for (let pos = 0; pos < (mappedLines || []).length; pos++) {
      let ml = mappedLines[pos];
      let ui = (pos < uiLines.length) ? uiLines[pos] : null;

      let qty = Number(ml.quantity) || 0;
      let unityAmount = Number(ml.unityAmount) || 0;
      let discount = Number(ml.discount) || 0;

      let netPrice = unityAmount * qty;

      let apiTaxes = this.buildApiTaxesFromUiSelection(ui ? ui.tax : null);

      let lineDto: any = {
        product: ml.product,
        conceptUid: ml.conceptUid,

        quantity: qty,
        discount: discount,
        unityAmount: unityAmount,

        netPrice: netPrice,

        position: pos,
        totalLine: Number(ml.totalLine) || 0,

        taxes: apiTaxes,

        invoiceLineGroup: ml.groupClientKey,
      };

      if (isMeasurableFlag === true) {
        lineDto.isMeasurable = true;
        lineDto.masterUnitMeasurableUid = ui && ui.masterUnitMeasurableUid ? ui.masterUnitMeasurableUid.id : null;

        if (ui && ui.quantityMeasurable != null) lineDto.quantityMeasurable = String(ui.quantityMeasurable);
        else lineDto.quantityMeasurable = null;
      } else {
        lineDto.masterUnitMeasurableUid = null;
        lineDto.isMeasurable = false;
        lineDto.quantityMeasurable = 1;
      }

      result.push(lineDto);
    }

    return result;
  }

  private buildApiTaxesFromUiSelection(selectedTaxes: any[]): any[] {
    let res: any[] = [];
    let sel = selectedTaxes || [];

    for (let i = 0; i < sel.length; i++) {
      let t = sel[i];
      if (!t) continue;

      let pickedId = null;
      if (t.id != null) pickedId = t.id;
      else if (t.uid != null) pickedId = t.uid;
      else if (t.value != null) pickedId = t.value;

      let full = null;
      for (let j = 0; j < (this.taxes || []).length; j++) {
        if (this.taxes[j] && this.taxes[j].id === pickedId) { full = this.taxes[j]; break; }
      }

      let rate = 0;
      let type = '';
      let alias = '';
      let name = '';
      let taxId = null;

      if (full) {
        alias = full.display || '';
        if (full.metadata) {
          if (full.metadata.import != null) rate = full.metadata.import;
          if (full.metadata.type) type = full.metadata.type;
          if (full.metadata.id != null) taxId = full.metadata.id;
          if (full.metadata.name != null) name = full.metadata.name;
        }
      }

      if (!alias && t.alias) alias = t.alias;
      if (!type && t.type) type = t.type;

      let dto: any = {
        name: name,
        alias: alias,
        taxImport: rate,
        type: type,
        taxId: taxId,
        id: taxId,
        uid: full.id
      };

      if (t.uid) dto.uid = t.uid;
      if (t.id != null) dto.id = taxId;

      res.push(dto);
    }

    return res;
  }

  private isMeasurableEnabled(): boolean {
    if (!this.form) return false;
    let c = this.form.get('isMeasurable');
    return !!(c && c.value === true);
  }

  private getBaseForLine(v: any): number {
    if (!v || v.type !== 'line') return 0;

    let qty = Number(v.quantity) || 0;
    let price = Number(v.price) || 0;
    let disc = Number(v.discount) || 0;

    let measurable = this.isMeasurableEnabled();

    let qm = 1;
    if (measurable) {
      qm = Number(v.quantityMeasurable);
      if (!qm || qm <= 0) qm = 1;
    }

    let qtyReal = measurable ? (qty * qm) : qty;

    return price * qtyReal * (1 - disc / 100);
  }

  recomputeTotals(): void {
    const vals = (this.items && this.items.value) ? this.items.value : [];

    let subtotal = 0;
    let taxesTotal = 0;
    let retentionsTotal = 0;
    let hasIVA = false;
    let hasIGIC = false;
    let hasAIEM = false;
    const lineTotals: number[] = [];

    for (let i = 0; i < vals.length; i++) {
      const v = vals[i];
      if (!v || v.type !== 'line') {
        lineTotals.push(0);
        continue;
      }

      const base = this.getBaseForLine(v);
      subtotal += base;

      let lineSum = base;
      const taxes = v.tax || [];
      for (let j = 0; j < taxes.length; j++) {
        const full = this.findTaxFull(taxes[j]);
        const rate = full && full.metadata && full.metadata.import != null ? full.metadata.import : 0;
        const amount = base * (rate / 100);
        lineSum += amount;

        if (rate > 0) taxesTotal += amount;
        else if (rate < 0) retentionsTotal += amount;

        if (full && full.metadata && full.metadata.type != null) {
          const text = String(full.metadata.type).toUpperCase();
          if (text.indexOf('AIEM') !== -1) hasAIEM = true;
          if (text.indexOf('IGIC') !== -1) hasIGIC = true;
          if (text.indexOf('IVA') !== -1) hasIVA = true;
        }
      }

      lineTotals.push(lineSum);
    }

    this.subtotal = subtotal;
    this.taxesTotal = taxesTotal;
    this.retentionsTotal = retentionsTotal;
    this.total = subtotal + taxesTotal + retentionsTotal;
    this.lineTotals = lineTotals;
    this.taxesLabel = hasIVA ? 'IVA' : hasIGIC ? 'IGIC' : hasAIEM ? 'AIEM' : 'Impuestos';
  }

  private wireTotals(): void {
    this.form.valueChanges
      .pipe(debounceTime(150), takeUntil(this.destroy$))
      .subscribe(() => this.recomputeTotals());
  }

  private buildTaxIndex(): void {
    this.taxById = new Map<any, any>();
    for (const tax of this.taxes || []) {
      if (tax && tax.id != null) this.taxById.set(tax.id, tax);
    }
  }

  private findTaxFull(tx: any): any {
    const id = PreinvoiceFormUtils.pickId(tx);
    if (id == null) return null;
    const found = this.taxById.get(id);
    return found != null ? found : null;
  }

  expand(): void { }
  onCancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
      return;
    }
    this.router.navigate(['invoice/recurrents']);
  }

  onConceptChange(line: any) {
    let self = this;

    const concept = self.concepts.filter(c => c.id == line.get('conceptUid').value.id)[0] as MasterConceptModel;

    if (concept) {
      line.get('serieConceptUid').setValue(concept.id);

      if (concept.metadata.serialNumber) {
        this.conceptService.loadData(concept.id).then(response => {
          const dialogRef = this.dialog.open(ConceptSerialNumberComponent, {
            width: '640px',
            maxWidth: '96vw',
            height: '70%',
            data: {
              concept: response,
              live: true
            }
          });

          dialogRef.afterClosed().subscribe(result => {
            if (result) {
              const current = line.get('conceptUid').value;
              line.get('conceptUid').setValue({ ...current, display: current.display + " - S/N: " + result });
            }
          });
        });
      }

      line.get('quantity').setValue(1);
      line.get('price').setValue(concept.metadata.netPrice);
      line.get('discount').setValue(concept.metadata.discount);

      const conceptTaxes = [];

      concept.metadata.taxes.forEach(t => {
        const tax = self.taxes.filter(c => c.metadata.id == t.taxId)[0];
        if (tax) conceptTaxes.push(tax);
      });

      if (conceptTaxes.length === 0) {

        if (self.contactTaxClientUid) {
          const tax1 = self.taxes.filter(t => t.id === self.contactTaxClientUid)[0];
          if (tax1) conceptTaxes.push(tax1);
        }
        if (self.contactTaxClientSecondUid) {
          const tax2 = self.taxes.filter(t => t.id === self.contactTaxClientSecondUid)[0];
          if (tax2) conceptTaxes.push(tax2);
        }
      }

      line.get('tax').setValue(conceptTaxes);
    } else {
      const contactTaxes = self.getContactDefaultTaxes();
      if (contactTaxes.length > 0) {
        line.get('tax').setValue(contactTaxes);
      }
    }
  }

  private getContactDefaultTaxes(): any[] {
    const taxes: any[] = [];
    if (this.contactTaxClientUid) {
      const tax1 = this.taxes.filter(t => t.id === this.contactTaxClientUid)[0];
      if (tax1) taxes.push(tax1);
    }
    if (this.contactTaxClientSecondUid) {
      const tax2 = this.taxes.filter(t => t.id === this.contactTaxClientSecondUid)[0];
      if (tax2) taxes.push(tax2);
    }
    return taxes;
  }

  onPaymethodChange() {
    this.applyPaymentValidators();
  }

  onContactChange(selectedValue: any) {
    const contact = this.contacts.filter(c => c.id == selectedValue.id)[0];

    if (contact) {
      this.assingDataContact();
      return;
    }

    ContactFormV2Component.open(this.dialog, { scratch: { companyName: selectedValue.id, isClient: true } })
      .pipe(
        delay(1200)
      )
      .subscribe(result => {
        this.lookupsFacade.loadContacts$()
          .pipe(takeUntil(this.destroy$))
          .subscribe((list: any[]) => {
            this.contacts = list || [];

            const contact = this.contacts.filter(c => result && result.id && String(c.id).toLowerCase() === String(result.id).toLowerCase())[0]
              || this.contacts.filter(c => c.id == selectedValue.id)[0];

            if (result && result.id && contact) {
              this.form.patchValue({
                contactUid: {
                  id: contact.id,
                  name: contact.display
                }
              });
              this.assingDataContact();
            }
          });
      });
  }

  assingDataContact() {
    let self = this;

    const contactRequest = this.contactService.loadData(self.form.get('contactUid').value.id)
      .then(response => this.ensurePaymentLookups(response ? response.paymentTypeClient : '').then(() => response));

    this.loading.run('Cargando datos del contacto…', contactRequest).then(response => {
      try {
        const contactSelected = response;
        const entranceAccount = response.entranceAccount;

        let account = null;
        if (Utils.IsNullOrEmpty(entranceAccount)) {
          if (self.userCompany.isSalesInvoice) {
            account = self.accountingModels.filter(c => c.metadata != null && c.metadata.number == '7000000001')[0];
          }

          if (self.userCompany.isServiceInvoice) {
            account = self.accountingModels.filter(c => c.metadata != null && c.metadata.number == '7050000001')[0];
          }

          if (self.userCompany.isSalesInvoice && self.userCompany.isServiceInvoice) {
            account = self.accountingModels.filter(c => c.metadata != null && c.metadata.number == '7000000001')[0];
          }
        } else {
          account = self.accountingModels.filter(c => c.display == response.entranceAccount)[0];
        }

        if (account) {
          self.form.patchValue({ entranceAccount: { id: account.id, display: account.display } });
        }

        self.form.patchValue({ payMethodUid: { id: response.paymentTypeClient, display: response.paymentTypeClient } });

        if (contactSelected.paymentClientUid != null && self.isBankAccountPayment()) {
          const bankAccount = self.bankAccounts.filter(c => c.id == response.paymentClientUid)[0];
          self.form.patchValue({ bankAccountUid: { id: bankAccount.id, display: bankAccount.display } });
        }

        if (contactSelected.clientEfectivoId != null && self.isCashRegisterPayment()) {
          const cashRegister = self.cashRegisters.filter(c => c.metadata.id == response.clientEfectivoId)[0];
          self.form.patchValue({ cashRegisterUid: { id: cashRegister.id, display: cashRegister.display } });
        }

        if (contactSelected.clientPartnersId != null && self.isPartnerPayment()) {
          const partner = self.partners.filter(c => c.metadata.id == response.clientPartnersId.toString())[0];
          self.form.patchValue({ partnerUid: { id: partner.id, display: partner.display } });
        }

        self.contactExpirationDays = contactSelected.expirationClient != null ? Number(contactSelected.expirationClient) : null;
        const release = self.form.get('recurrentStart').value;
        if (release && moment(release).isValid()) {
          self.form.get('invoiceExpiration').setValue(moment(release).add(self.contactExpirationDays || 0, 'days').toDate());
        }

        self.contactTaxClientUid = contactSelected.taxClientUid || null;
        self.contactTaxClientSecondUid = contactSelected.taxClientSecondUid || null;
      } catch (error) {
        this.notification.errorBottom('Se ha producido un error al asignar los datos del contacto. Por favor, revise y complete la información requerida.')
      }

      self.loadCentersForContact(self.form.get('contactUid').value.id);
    }, () => {
      self.notification.errorBottom('No se pudieron cargar los datos del contacto.');
    });
  }

  loadCentersForContact(contactUid: string) {
    let self = this;
    if (!contactUid) {
      self.centers = [];
      return;
    }

    this.lookupsFacade.loadCenters$(contactUid)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        function (list: any[]) {
          self.centers = list || [];
          if (self.centers.length === 1) {
            self.form.patchValue({ centerUid: { id: self.centers[0].id, display: self.centers[0].display } });
          }
        },
        function () {
          self.centers = [];
        }
      );
  }

  private loadCentersForContactAsync(contactUid: string): Promise<void> {
    let self = this;
    if (!contactUid) {
      self.centers = [];
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      this.lookupsFacade.loadCenters$(contactUid)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          function (list: any[]) {
            self.centers = list || [];
            resolve();
          },
          function () {
            self.centers = [];
            resolve();
          }
        );
    });
  }

  loadActivity() {
    let self = this;

    this.activityService.loadData(ActualSelected.activitySelected).then(activity => {
      self.form.get('recurrentNumber').setValue(activity.recurrentNumber);
      self.form.get('applyProject').setValue(activity.isProject);
      if (activity.isProject) self.ensureProjects();
      self.isLoading = false;
    });
  }

  private lastValidTaxesByLineKey: any = {};
  private prevTaxesByLineKey: any = {};

  private getLineKey(line: any): string {
    if (line && line.value && line.value.uid) return String(line.value.uid);

    if (line && !line.__taxKey) {
      line.__taxKey = 'l_' + Math.random().toString(16).slice(2) + '_' + new Date().getTime();
    }
    return line ? String(line.__taxKey) : 'unknown';
  }

  handleTaxFocus = (lineIndex: number): void => {
    const line = this.items.at(lineIndex);
    if (!line) return;
    let c = line.get('tax');
    if (!c) return;
    let key = this.getLineKey(line);
    this.prevTaxesByLineKey[key] = JSON.parse(JSON.stringify(c.value || []));
  }

  handleTaxChange = (value: any, lineIndex: number): string => {
    const line = this.items.at(lineIndex);
    if (!line) return '';

    let c = line.get('tax');
    if (!c) return '';

    let key = this.getLineKey(line);
    let current = c.value || [];

    if (!this.lastValidTaxesByLineKey[key]) {
      this.lastValidTaxesByLineKey[key] = JSON.parse(JSON.stringify(current));
    }

    let validation = this.validateTaxesSelection(current);

    if (!validation.ok) {
      if (this.prevTaxesByLineKey[key]) {
        c.setValue(JSON.parse(JSON.stringify(this.prevTaxesByLineKey[key])), { emitEvent: true });
      } else {
        c.setValue(JSON.parse(JSON.stringify(this.lastValidTaxesByLineKey[key])), { emitEvent: true });
      }
      if (validation.message) this.notification.errorBottom(validation.message);
      return '';
    }

    this.lastValidTaxesByLineKey[key] = JSON.parse(JSON.stringify(current));
    return '';
  }

  private validateTaxesSelection(selected: any[]): { ok: boolean; message: string } {
    let list = selected || [];

    let used: any = {};
    let mainTypeUsed: any = {};

    for (let i = 0; i < list.length; i++) {
      let tx = list[i];
      if (!tx) continue;

      let id = this.getTaxId(tx);
      if (id == null) continue;

      if (used[id]) {
        return { ok: false, message: 'Este impuesto ya ha sido aplicado.' };
      }

      let type = this.getTaxType(tx);
      let isRecargo = this.isRecargoEquivalencia(tx);

      if (!isRecargo && type) {
        if (mainTypeUsed[type]) {
          return { ok: false, message: 'Ya se ha aplicado un impuesto del tipo ' + type + '.' };
        }
        mainTypeUsed[type] = true;
      }

      used[id] = true;
    }

    return { ok: true, message: '' };
  }

  private getTaxId(t: any): any {
    if (!t) return null;
    if (t.id != null) return t.id;
    if (t.uid != null) return t.uid;
    if (t.value != null) return t.value;
    return null;
  }

  private getTaxName(t: any): string {
    if (!t) return '';
    if (t.name) return String(t.name);
    if (t.display) return String(t.display);
    return '';
  }

  private getTaxType(t: any): string {
    if (!t) return '';
    if (t.type) return String(t.type);
    if (t.metadata && t.metadata.type) return String(t.metadata.type);
    return '';
  }

  private isRecargoEquivalencia(t: any): boolean {
    return t.metadata.name.toLowerCase().indexOf('recargo equiv') >= 0 || t.metadata.name.toLowerCase().indexOf('recargo de equiv') >= 0;
  }

  trackByIndex(index: number): number {
    return index;
  }
}
